import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useRef } from 'react';
import { Folder, MoreVertical, Edit2, Trash2, FolderInput } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DeleteFolderModal } from '@/components/hierarchical-folders/DeleteFolderModal';
import { HierarchicalFolder } from '@/types/hierarchicalFolders';
import { cn } from '@/lib/utils';

// 128400 → "128.400"
const formatMetric = (n: number) => n.toLocaleString(getInterfaceLanguage());

interface FolderCardProps {
  folder: HierarchicalFolder;
  /** Métricas agregadas da pasta (soma dos vídeos). Vêm do backend via folder.totalPlays / folder.totalViews. */
  stats?: { plays: number; views: number };
  /** Qual métrica exibir na segunda linha — acompanha a ordenação escolhida no dashboard. */
  metric?: 'plays' | 'views';
  onClick?: () => void;
  /** ATENÇÃO: recebe o NOVO nome (edição inline dentro do card), não o nome atual. */
  onRename?: (id: string, newName: string) => void | Promise<void>;
  onDelete?: (id: string) => void | Promise<void>;
}

export function FolderCard({
  folder,
  stats,
  metric = 'plays',
  onClick,
  onRename,
  onDelete,
}: FolderCardProps) {
  useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(folder.name);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditingName(folder.name);
  };

  const handleSaveEdit = () => {
    const trimmedName = editingName.trim();
    if (trimmedName && trimmedName !== folder.name) {
      onRename?.(folder.id, trimmedName);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditingName(folder.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const { setNodeRef, isOver } = useDroppable({
    id: `folder-${folder.id}`,
    data: {
      type: 'folder',
      folderId: folder.id,
    },
  });

  return (
    <motion.div
      ref={setNodeRef}
      className={cn(
        'group relative',
        'w-full cursor-pointer',
        'bg-white rounded-[12px] px-[14px] py-[12px] h-[72px] min-h-[72px]',
        'border border-[#E7ECF3]',
        'shadow-[0_1px_2px_rgba(16,24,40,.04)]',
        'focus-within:border-[#BCD0FA]',
        'transition-all duration-[180ms] ease-out',
        isOver && 'border-[#3478F6] bg-[#F2F6FF] ring-[3px] ring-[#3478F6]/10'
      )}
      onClick={() => {
        if (!isEditing) {
          onClick?.();
        }
      }}
    >
      {/* Indicador visual de drop */}
      {isOver && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 rounded-[12px] border-2 border-dashed border-[#3478F6] bg-[#F2F6FF]/80 flex items-center justify-center pointer-events-none z-10"
        >
          <div className="flex items-center gap-2 text-[#3478F6] font-semibold">
            <FolderInput className="h-4 w-4" />
            <span className="text-[12px] uppercase tracking-wider">{translateInterfaceText("Soltar aqui")}</span>
          </div>
        </motion.div>
      )}

      <div className="flex items-center gap-[10px] h-full">
        {/* 1. Ícone */}
        <div className="relative w-[34px] h-[34px] flex-shrink-0 flex items-center justify-center">
          <div className="absolute inset-0 rounded-[8px] bg-[#F0F5FF]" />
          <Folder
            className="relative w-[18px] h-[18px] text-[#3478F6]"
            strokeWidth={2}
          />
        </div>

        {/* 2. Bloco de texto (nome + métrica) ou edição inline */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'font-semibold text-[14px] text-foreground',
                'bg-background border-2 border-primary rounded-md',
                'px-2 py-0.5 -ml-2',
                'outline-none',
                'w-full'
              )}
            />
          ) : (
            <>
              <h3
                className="font-medium text-[13.5px] text-[#101828] leading-tight truncate mb-[1px]"
                title={folder.name}
              >
                {folder.name}
              </h3>
              <div className="flex items-center text-[11.5px] font-medium text-[#667085] whitespace-nowrap overflow-hidden">
                <span className="whitespace-nowrap">
                  {formatMetric(metric === 'plays' ? (stats?.plays ?? 0) : (stats?.views ?? 0))}{' '}
                  {metric === 'plays' ? translateInterfaceText("plays") : translateInterfaceText("visualizações")}
                </span>
              </div>
            </>
          )}
        </div>

        {/* 3. Menu de ações */}
        <div className="flex-shrink-0 ml-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button
                className={cn(
                  'opacity-100 cursor-pointer',
                  'w-8 h-8 min-w-[32px] min-h-[32px] -mr-1 rounded-lg',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#BCD0FA]',
                  'flex items-center justify-center',
                  'hover:bg-foreground/[0.04] transition-all duration-200'
                )}
                aria-label={translateInterfaceText("Menu da pasta")}
              >
                <MoreVertical className="h-4 w-4 text-[#667085]" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 z-50">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEditing();
                }}
                className="cursor-pointer focus:bg-[#3478F6] focus:text-white data-[highlighted]:bg-[#3478F6] data-[highlighted]:text-white [&_svg]:focus:text-white [&_svg]:data-[highlighted]:text-white"
              >
                <Edit2 className="mr-2 h-4 w-4" />
                {translateInterfaceText("Renomear")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteOpen(true);
                }}
                className="text-red-600 data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {translateInterfaceText("Excluir")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DeleteFolderModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        folderNames={[folder.name]}
        onConfirm={async () => {
          await onDelete?.(folder.id);
        }}
      />
    </motion.div>
  );
}
